import React, { useEffect, useState, useCallback, useRef } from "react";
import baseUrl from "../../../api/baseUrl";
import AppointmentCounts from "../../appointment/viewappointment/components/AppointmentCounts";
import AppointmentFilter from "../../appointment/viewappointment/components/AppointmentFilter";
import AppointmentTr from "../../appointment/viewappointment/components/AppointmentTr";
import TabBar from "../../../components/tabbar/TabBar";
import BottomTab from "../../../components/pagination/BottomTab";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import socket from "../../../api/socket";

const LIMIT = 20;
const MemoizedAppointmentTr = React.memo(AppointmentTr);

function MyAppointment() {
  const [appointment, setAppointment] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState({
    appointments: 0,
    cancelled: 0,
    completed: 0,
    pending: 0,
    inqueue: 0,
    reschudle: 0,
    ongoing: 0,
    noshow: 0,
  });
  const [all, setAll] = useState("all");
  const [selectedOption, setSelectedOption] = useState<string>("pending");
  const pageRef = useRef(1);
  pageRef.current = page;

  const getAppointments = useCallback(
    (status: any, dayFilter: string, pageNum: number = 1) => {
      baseUrl
        .get("/appointment/findAllAppointmentDentistId", {
          headers: {
            status: status,
            day: dayFilter,
            page: pageNum,
            limit: LIMIT,
          },
        })
        .then((response) => {
          setAppointment(response.data.data || []);
          setTotalCount(response.data.count ?? 0);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    []
  );

  const handleOptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSelectedOption(value);
      setPage(1);
      getAppointments(value, all, 1);
    },
    [all, getAppointments]
  );

  const handleDay = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setAll(value);
      setPage(1);
      getAppointments(selectedOption, value, 1);
    },
    [selectedOption, getAppointments]
  );

  const onPageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      getAppointments(selectedOption, all, newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [selectedOption, all, getAppointments]
  );

  const appointmentCount = useCallback(() => {
    baseUrl
      .get("/appointment/appointmentscountsbyId", {
        headers: { status: true },
      })
      .then((response) => {
        setCount({
          appointments: response.data?.appointments,
          cancelled: response.data?.cancelled,
          completed: response.data?.completed,
          pending: response.data?.pending,
          inqueue: response.data?.inqueue,
          reschudle: response.data?.reschudle,
          noshow: response.data?.noshow,
          ongoing: response.data?.ongoing,
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const appointmStatus = useCallback(() => {
    getAppointments(selectedOption, all, pageRef.current);
    appointmentCount();
  }, [getAppointments, appointmentCount, selectedOption, all]);

  useEffect(() => {
    appointmStatus();
  }, [appointmStatus]);

  return (
    <>
      <TabBar title="My Appointments" />
      <div className="mt-6 space-y-4">
        <AppointmentCounts count={count} />
        <div className="bg-white dark:bg-slate-800 rounded-xl md:rounded-2xl shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-4 md:p-6">
          <AppointmentFilter
            selectedOption={selectedOption}
            all={all}
            handleOptionChange={handleOptionChange}
            handleDay={handleDay}
          />

          {/* Desktop Table */}
          <div className="hidden md:block overflow-hidden rounded-xl border border-slate-200 dark:border-slate-600">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-700 dark:to-slate-800 border-b-2 border-slate-200 dark:border-slate-600">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
                    Patient Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
                    Appointment Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
                    Time Slot
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
                    Purpose
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider w-32">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-600">
                {appointment.map((item: any, index: number) => (
                  <MemoizedAppointmentTr
                    appointmStatus={appointmStatus}
                    key={index}
                    item={item}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {appointment.map((item: any, index: number) => (
              <div
                key={index}
                className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 p-4 space-y-3 hover:shadow-md dark:hover:bg-slate-700/50 transition-shadow"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base text-slate-900 dark:text-slate-100 mb-1 truncate">
                      {item?.patientId?.fullName || item?.bookPatientId?.fullName || "N/A"}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {item?.date?.split("T")[0]}
                    </p>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    {item?.patientId?.fullName && (
                      <>
                        <button
                          onClick={() =>
                            (window.location.href = `/client/patients/patientone/general-detail/${item?.patientId?._id}`)
                          }
                          className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 flex items-center justify-center hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-colors"
                          title="View Patient"
                        >
                          <RemoveRedEyeIcon sx={{ fontSize: 18 }} />
                        </button>
                        <button
                          onClick={(e: any) => {
                            e.stopPropagation();
                            baseUrl
                              .post("treatment/create-treatment", {
                                patientId: item?.patientId?._id,
                              })
                              .then((response) => {
                                window.location.href = `/client/treatment-diagnose/add-treatment/diagnose/${response.data.data._id}`;
                              })
                              .catch((error) => console.error(error));
                          }}
                          className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 flex items-center justify-center hover:bg-emerald-100 dark:hover:bg-emerald-900/60 transition-colors"
                          title="Add Treatment"
                        >
                          <MedicalInformationIcon sx={{ fontSize: 18 }} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs text-slate-500 dark:text-slate-400 block mb-1">
                      Time Slot
                    </span>
                    <span className="px-2.5 py-1 bg-primary/10 dark:bg-primary/30 text-primary dark:text-sky-300 rounded-md text-xs font-medium inline-block">
                      {item?.startTime} - {item?.endTime}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 dark:text-slate-400 block mb-1">
                      Status
                    </span>
                    <select
                      onChange={(e: any) => {
                        const status = e.target.value;
                        socket.emit("allAppointmentStatus", {
                          appointmentid: item?._id,
                          status: status,
                        });
                        baseUrl
                          .patch("/appointment/statusAppointmentById", {}, {
                            headers: {
                              appointmentid: item?._id,
                              status: status,
                            },
                          })
                          .then(() => {
                            appointmStatus();
                          })
                          .catch((error) => console.log(error));
                      }}
                      value={item?.status}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium border cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 w-full ${
                        item?.status === "ongoing"
                          ? "bg-lime-100 dark:bg-lime-900/40 text-lime-700 dark:text-lime-300 border-lime-300 dark:border-lime-600"
                          : item?.status === "inqueue"
                          ? "bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 border-sky-300 dark:border-sky-600"
                          : item?.status === "canceled"
                          ? "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-300 dark:border-red-600"
                          : item?.status === "completed"
                          ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-600"
                          : item?.status === "appointment"
                          ? "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-600"
                          : item?.status === "reschudle"
                          ? "bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-600"
                          : item?.status === "noshow"
                          ? "bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-500"
                          : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"
                      }`}
                    >
                      <option value="">Select</option>
                      <option value="appointment">Upcoming</option>
                      <option value="completed">Completed</option>
                      <option value="canceled">Canceled</option>
                      <option value="inqueue">In Queue</option>
                      <option value="ongoing">Ongoing</option>
                      <option value="noshow">No Show</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs text-slate-500 dark:text-slate-400 block mb-1">
                      Purpose
                    </span>
                    <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">
                      {item?.purposeId?.purposeName ||
                        item?.bookTreatmentId?.name ||
                        "N/A"}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 dark:text-slate-400 block mb-1">
                      Type
                    </span>
                    <span
                      className={`px-2.5 py-1 rounded-md text-xs font-medium inline-block ${
                        item?.type === "online"
                          ? "bg-primary/10 dark:bg-primary/30 text-primary"
                          : "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
                      }`}
                    >
                      {item?.type || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {appointment.length === 0 && (
              <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                <p className="text-sm">No appointments found</p>
              </div>
            )}
          </div>

          {totalCount > LIMIT && (
            <BottomTab
              title="Appointments"
              count={totalCount}
              onPageChange={onPageChange}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default React.memo(MyAppointment);
